import axios from "axios";

const baseAPI = process.env.REACT_APP_API_URL;
const deadlineBaseURL = `${baseAPI}/v2/deadline`;
const deadlineTypes = new Set(["next", "future", "completed", "all"]);
const listFilters = new Set([
  "page",
  "pageSize",
  "search",
  "assignedTo",
  "customerIds",
  "customer",
  "startDate",
  "endDate",
]);

export const normalizeDeadline = deadline => {
  if (!deadline || typeof deadline !== "object") return deadline;

  const { entity } = deadline;
  if (entity?.entityType && entity?.entityId) {
    return {
      ...deadline,
      entity: { type: entity.entityType, id: entity.entityId },
    };
  }

  return deadline;
};

export const getDeadlineErrorMessage = error => {
  const data = error?.response?.data;

  if (Array.isArray(data?.errors) && data.errors.length) {
    return data.errors
      .map(item =>
        typeof item === "string"
          ? item
          : item?.message || item?.error || JSON.stringify(item)
      )
      .join("; ");
  }

  return data?.error || data?.message || error?.message || "Erro ao processar prazo";
};

const request = async callback => {
  try {
    return await callback();
  } catch (error) {
    const normalizedError = new Error(getDeadlineErrorMessage(error));
    normalizedError.cause = error;
    normalizedError.response = error?.response;
    throw normalizedError;
  }
};

const normalizeFilterValue = (key, value) => {
  if (!Array.isArray(value)) return value;

  const ids = value
    .map(item =>
      key === "assignedTo"
        ? item?.User?.id ?? item?.user_id ?? item?.id ?? item
        : item?.id ?? item
    )
    .filter(Boolean);

  return [...new Set(ids)].join(",");
};

export const buildDeadlineListParams = (params = {}) =>
  Object.entries(params).reduce((result, [key, value]) => {
    if (!listFilters.has(key) || value === undefined || value === null || value === "") {
      return result;
    }

    const finalKey = key === "customer" ? "customerIds" : key;
    const normalizedValue = normalizeFilterValue(key, value);
    if (normalizedValue !== "") result[finalKey] = normalizedValue;
    return result;
  }, {});

export const amountOpenDeadlines = async (isCompleted = false) => {
  const response = await request(() =>
    axios.get(
      isCompleted
        ? `${deadlineBaseURL}/count/info?isCompleted=true`
        : `${deadlineBaseURL}/count/info`
    )
  );
  return response.data;
};

// @deprecated Substituída por getDeadlinesByCnjMonitoring, que devolve a
// lista e a contagem (openCount) na mesma resposta. Único consumidor
// restante: getLawsuitDetails, usado pela ficha antiga do processo.
export const getDeadlineCnjInfo = async cnjMonitoringId => {
  const response = await request(() =>
    axios.get(`${deadlineBaseURL}/count/cnjInfo/${cnjMonitoringId}`)
  );
  return response.data;
};

export const getDeadlinesByCnjMonitoring = async (cnjMonitoringId, { status } = {}) => {
  const query = status ? `?${new URLSearchParams({ status })}` : "";
  const response = await request(() =>
    axios.get(`${deadlineBaseURL}/list/cnjInfo/${cnjMonitoringId}${query}`)
  );
  return response.data;
};

export const createDeadline = async payload => {
  const response = await request(() =>
    axios.post(`${deadlineBaseURL}/create`, { ...payload })
  );
  return normalizeDeadline(response.data);
};

export const updateDeadline = async (deadlineId, payload) => {
  const response = await request(() =>
    axios.put(`${deadlineBaseURL}/${deadlineId}`, payload)
  );
  return normalizeDeadline(response.data);
};

export const getDeadlineDetail = async deadlineId => {
  const response = await request(() =>
    axios.get(`${deadlineBaseURL}/detail/${deadlineId}`)
  );
  return normalizeDeadline(response.data);
};

export const deleteDeadline = async deadlineId => {
  await request(() => axios.delete(`${deadlineBaseURL}/${deadlineId}`));
};

export const getDeadlines = async (type, params = {}) => {
  if (!deadlineTypes.has(type)) {
    throw new Error(`Tipo de listagem de prazo inválido: ${type}`);
  }

  const query = new URLSearchParams(buildDeadlineListParams(params)).toString();
  const response = await request(() =>
    axios.get(`${deadlineBaseURL}/list/${type}${query ? `?${query}` : ""}`)
  );
  return response.data;
};

export const checkOpenDeadlines = async () => {
  const response = await request(() =>
    axios.get(`${deadlineBaseURL}/checkOpenDeadlines`)
  );
  return response.data;
};
